import { isElite, type EntitySnapshot, type Rank } from '../net/protocol';

/**
 * Who gets a nameplate, and how much of one.
 *
 * Promoted from `ideas/NAMEPLATE-HEALTHBAR-VISIBILITY-HERARCHY.md`, whose complaint is exact:
 * fifteen to twenty mobs in a tight space, each with a name *and* a bar, is a wall of red lines
 * and overlapping text that hides the thing the player is actually reading — where the monsters
 * are standing.
 *
 * Every mob used to get both, always. The rule now is that **an undamaged, unhovered trash mob
 * gets nothing**, so walking up to a pack shows the pack.
 *
 * Pure and separate from the component so the hierarchy can be tested without a DOM, a camera or
 * a projection — `Nameplates.tsx` renders what this decides and makes no judgements of its own.
 */

/**
 * How long a trash mob's bar stays up after the last hit, in milliseconds.
 *
 * The idea says three seconds and three seconds is right: long enough to read the result of an
 * AoE across a pack, short enough that the screen is clean again before the next engagement.
 *
 * **Not `DAMAGE_TTL_MS`** (900), which is how long a floating *number* lives. They answer
 * different questions — a number is a discrete event and a bar is a state — and tying the bar to
 * the number would have made it flick out mid-fight between hits of a slow weapon.
 */
export const DAMAGE_MEMORY_MS = 3_000;

/**
 * How long the bar spends fading, at the end of that window.
 *
 * Inside the fade the bar is still rendered, at falling opacity. A bar that vanished on a frame
 * boundary reads as a rendering fault; one that fades reads as the fight moving on.
 */
export const BAR_FADE_MS = 600;

/**
 * Beyond this many world units, a trash mob's plate is not drawn at all.
 *
 * `MobVisionRadius` is 26 — the server sends dormant packs from well outside the range at which
 * anything can be fought — so without a gate here the far half of a zone contributes bars for
 * monsters the player has not met. Elites are exempt: knowing a champion is over there is the
 * point of showing one at all.
 *
 * 18 rather than 26 because that is roughly a screen: past it a plate is small, crowded against
 * the edge, and about a fight that has not started.
 */
export const TRASH_PLATE_RANGE = 18;

/** How much smaller a trash bar is drawn than an elite's. Straight from the idea. */
export const TRASH_BAR_SCALE = 0.7;

/** What the HUD should draw for one mob this frame. */
export interface PlateVerdict {
  /** The floating name, and the trait tags that go with it. */
  showName: boolean;
  /** The health bar. */
  showBar: boolean;
  /** 1 for a full-size bar, `TRASH_BAR_SCALE` for a trash one. */
  scale: number;
  /** 0–1, for the tail of the damage window. Always 1 while something keeps the plate up. */
  opacity: number;
}

/** Everything the rules need that is not on the entity itself. */
export interface PlateContext {
  /** The pointer is over this mob. */
  hovered: boolean;
  /** It is the locked-on target. */
  targeted: boolean;
  /** `performance.now()` when it was last hit, or undefined for never. */
  damagedAt: number | undefined;
  /** The frame clock, in the same units as `damagedAt`. */
  now: number;
  /** World-space distance from the player, or undefined when that is unknown. */
  distance: number | undefined;
}

const HIDDEN: PlateVerdict = { showName: false, showBar: false, scale: 1, opacity: 0 };

/**
 * Ranks that always carry a full plate.
 *
 * `isElite` alone would be enough today — the server places only `NORMAL` and `ELITE` — but the
 * wire already has `UNIQUE` and `BOSS`, and the day one is placed it must not silently arrive as
 * trash. A set rather than `!== 'NORMAL'` so `MAGIC`, which the taxonomy calls a *step up from
 * trash and not a champion*, keeps the quiet treatment until somebody decides otherwise.
 */
const ALWAYS_PLATED: ReadonlySet<Rank> = new Set<Rank>(['ELITE', 'UNIQUE', 'BOSS']);

/** True for the ranks that keep a name and a bar at all times. */
export function isChampion(entity: Pick<EntitySnapshot, 'rank'>): boolean {
  return isElite(entity) || (entity.rank !== undefined && ALWAYS_PLATED.has(entity.rank));
}

/**
 * The whole hierarchy, in one function.
 *
 * Order matters and is the idea's own:
 *
 * 1. **Champions** — name and bar, always, at full size. A player has to be able to find the
 *    dangerous one in a crowd, which is the reason the crowd is being quietened.
 * 2. **Hovered or targeted** — name and bar, whatever the rank. This is the replacement for the
 *    always-on name: *point at it and it tells you what it is.*
 * 3. **Recently damaged trash** — bar only, no name, fading out over the last `BAR_FADE_MS` of
 *    the window. Casting an AoE lights up exactly what it hit.
 * 4. **Everything else** — nothing at all.
 *
 * Distance gates 3 and 4 only. A hovered mob is one the player is deliberately pointing at, and a
 * champion at range is information; a bar bobbing over an unengaged monster forty units away is
 * the clutter this exists to remove.
 */
export function plateVerdict(
  entity: Pick<EntitySnapshot, 'rank' | 'hp' | 'maxHp'>,
  ctx: PlateContext,
): PlateVerdict {
  if (isChampion(entity)) {
    return { showName: true, showBar: true, scale: 1, opacity: 1 };
  }

  if (ctx.hovered || ctx.targeted) {
    return { showName: true, showBar: true, scale: TRASH_BAR_SCALE, opacity: 1 };
  }

  if (ctx.distance !== undefined && ctx.distance > TRASH_PLATE_RANGE) return HIDDEN;

  // **Full health means no bar, even inside the damage window.** A mob healed back to full, or
  // one whose only "damage" was an avoided hit, has nothing to report. This is also what makes
  // walking into a fresh pack silent: they are all at `maxHp`.
  if (entity.hp >= entity.maxHp || ctx.damagedAt === undefined) return HIDDEN;

  const since = ctx.now - ctx.damagedAt;
  if (since < 0) {
    // A clock that went backwards — a reconnect, or a fixture. Treat it as just-hit rather than
    // as expired: the failure of a fresh bar is invisible, and the failure of a missing one is
    // the bug this file exists to prevent.
    return { showName: false, showBar: true, scale: TRASH_BAR_SCALE, opacity: 1 };
  }
  if (since >= DAMAGE_MEMORY_MS) return HIDDEN;

  const intoFade = since - (DAMAGE_MEMORY_MS - BAR_FADE_MS);
  const opacity = intoFade <= 0 ? 1 : Math.max(0, 1 - intoFade / BAR_FADE_MS);
  return { showName: false, showBar: true, scale: TRASH_BAR_SCALE, opacity };
}

/** True when the verdict asks for nothing, so the component can skip the element entirely. */
export function isBlank(verdict: PlateVerdict): boolean {
  return !verdict.showName && !verdict.showBar;
}
